import { useEffect, useMemo } from 'react';

import useStore from '@/state';
import addModelAction from '@/state/selectors/navigation/add-model';
import getModel from '@/state/selectors/navigation/get-model';
import { IStore } from '@/state/slices/typings';
import AuthoringHelper from '@/utils/authoring';
import Bootstrap from '@/utils/bootstrap';
import type { PageModel } from '@adobe/aem-react-editable-components';
import { EnvVarsService } from '@multitenancy-spa/env-vars-service/esm/services/env-vars-service';

const usePageModel = (
	fragmentVariationPath?: string,
	manipulator?: (pageModel: PageModel) => PageModel,
	mockedModel?: PageModel | undefined,
): PageModel | undefined | null => {
	const addModel = useStore(addModelAction);

	const canUseMocks =
		EnvVarsService.getVar('VITE_USE_MOCKS', true) &&
		!AuthoringHelper.isInEditor;

	const manipulatedModelPath = useMemo(() => {
		if (fragmentVariationPath) return fragmentVariationPath;
		return new URL(globalThis.location.href).pathname;
	}, [fragmentVariationPath]);

	const pageModel = useStore((state: IStore) =>
		getModel(state, manipulatedModelPath),
	);

	const manipulatedPageModel = useMemo(() => {
		if (!pageModel) return null;
		return manipulator?.(pageModel) ?? pageModel;
	}, [manipulator, pageModel]);

	const hasPageModel = useMemo(() => Boolean(pageModel), [pageModel]);

	useEffect(() => {
		if (
			!hasPageModel &&
			manipulatedModelPath &&
			manipulatedModelPath !== '/'
		) {
			Bootstrap.getInstance().setupModelPath(
				manipulatedModelPath,
				canUseMocks ? mockedModel : undefined,
				(model) => addModel(manipulatedModelPath, model),
			);
		}
	}, [
		addModel,
		canUseMocks,
		fragmentVariationPath,
		hasPageModel,
		manipulatedModelPath,
		mockedModel,
	]);

	return manipulatedPageModel;
};

export default usePageModel;
